/**
 * Shared helpers for legacy /uploads/{tenantId}/... URLs.
 * Used by Back Office blog editor and storefront resolveBlogImageUrl.
 */

const UPLOADS_TENANT_PATH_RE =
  /\/uploads\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|platform)(?:\/|$)/i;

/**
 * @param {string} url
 * @returns {string|null} lowercase tenant UUID from path, or null
 */
export function extractUploadsPathTenantId(url) {
  if (!url || typeof url !== 'string') return null;
  const m = url.match(UPLOADS_TENANT_PATH_RE);
  if (!m) return null;
  const head = m[1].toLowerCase();
  if (head === 'platform') {
    return '00000000-0000-0000-0000-000000000001';
  }
  return head;
}

/**
 * True when URL points at /uploads/{otherTenant}/... for a known current tenant.
 * Missing currentTenantId → fail open (cannot evaluate).
 */
export function isCrossTenantUploadsUrl(url, currentTenantId) {
  if (!currentTenantId) return false;
  const pathTenant = extractUploadsPathTenantId(url);
  if (!pathTenant) return false;
  return pathTenant !== String(currentTenantId).toLowerCase();
}

/**
 * Append ?t=<slug> when rewriting own-tenant legacy upload URLs so the
 * tenant-gated /uploads static middleware accepts the request.
 */
export function withUploadsTenantQuery(url, tenantSlug) {
  if (!url || !tenantSlug || typeof url !== 'string') return url;
  if (!/\/uploads\//i.test(url)) return url;
  try {
    const absolute = /^https?:\/\//i.test(url)
      ? new URL(url)
      : new URL(url, 'http://local.invalid');
    if (!absolute.searchParams.has('t')) {
      absolute.searchParams.set('t', tenantSlug);
    }
    if (/^https?:\/\//i.test(url)) {
      return absolute.toString();
    }
    return `${absolute.pathname}${absolute.search}`;
  } catch (_) {
    const sep = url.includes('?') ? '&' : '?';
    if (/[?&]t=/.test(url)) return url;
    return `${url}${sep}t=${encodeURIComponent(tenantSlug)}`;
  }
}

export default {
  extractUploadsPathTenantId,
  isCrossTenantUploadsUrl,
  withUploadsTenantQuery,
};
